import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { speichereInvestition, loescheInvestition } from 'modules/investitionen/features/investitionenSlice';

const KATEGORIEN = ['Aktien', 'ETF', 'Fonds', 'Krypto', 'Tagesgeld', 'Festgeld', 'Immobilien', 'P2P', 'Rohstoffe', 'Sonstiges'];
const WAEHRUNGEN = ['EUR', 'USD', 'CHF', 'GBP'];

const inputKlasse =
  'w-full rounded-lg border border-line bg-paper px-3 py-2 text-ink focus:border-brand-600 focus:outline-none';

/**
 * Modaler Editor fuer einen Investitionsort (Bank/Broker/Anlageklasse):
 * Institut, Kategorie, Waehrung, Anfangsbestand (Barvermoegen), Link und Notiz.
 * Positionen werden in der Detailseite gepflegt. Speichert verschluesselt.
 */
export default function InvestitionForm({ investition, onClose, onGeloescht }) {
  const dispatch = useDispatch();
  const [institut, setInstitut] = useState(investition.institut || '');
  const [kategorie, setKategorie] = useState(investition.kategorie || '');
  const [waehrung, setWaehrung] = useState(investition.waehrung || 'EUR');
  const [anfangsbestand, setAnfangsbestand] = useState(
    investition.anfangsbestand != null ? String(investition.anfangsbestand) : ''
  );
  const [link, setLink] = useState(investition.link || '');
  const [notiz, setNotiz] = useState(investition.notiz || '');
  const [fehler, setFehler] = useState(null);
  const [laeuft, setLaeuft] = useState(false);

  const istNeu = !investition.uuid;

  const absenden = async (e) => {
    e.preventDefault();
    if (!institut.trim()) {
      setFehler('Bitte ein Institut bzw. eine Bank angeben.');
      return;
    }
    setFehler(null);
    setLaeuft(true);
    try {
      await dispatch(
        speichereInvestition({
          uuid: investition.uuid,
          sortierung: investition.sortierung,
          positionen: investition.positionen,
          institut: institut.trim(),
          kategorie: kategorie.trim(),
          waehrung,
          anfangsbestand: Number(String(anfangsbestand).replace(',', '.')) || 0,
          link: link.trim(),
          notiz: notiz.trim(),
        })
      ).unwrap();
      onClose();
    } catch (ex) {
      setFehler('Speichern fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setLaeuft(false);
    }
  };

  const entfernen = async () => {
    if (!window.confirm('Diesen Investitionsort mit allen Positionen und Dokumenten wirklich löschen?')) return;
    setLaeuft(true);
    try {
      await dispatch(loescheInvestition(investition.uuid)).unwrap();
      if (onGeloescht) onGeloescht();
      else onClose();
    } catch (ex) {
      setFehler('Löschen fehlgeschlagen.');
      setLaeuft(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-paper p-6 shadow-xl">
        <h2 className="mb-4 font-serif text-xl font-semibold text-ink">
          {istNeu ? 'Investitionsort hinzufügen' : 'Investitionsort bearbeiten'}
        </h2>

        <form onSubmit={absenden} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-ink">Institut / Bank / Bezeichnung</label>
            <input
              value={institut}
              onChange={(e) => setInstitut(e.target.value)}
              placeholder="z. B. Trade Republic, ING, Coinbase, Eigentumswohnung"
              className={inputKlasse}
              autoFocus
            />
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label className="mb-1 block text-sm font-medium text-ink">Kategorie</label>
              <input
                list="investition-kategorien"
                value={kategorie}
                onChange={(e) => setKategorie(e.target.value)}
                placeholder="z. B. ETF"
                className={inputKlasse}
              />
              <datalist id="investition-kategorien">
                {KATEGORIEN.map((k) => (
                  <option key={k} value={k} />
                ))}
              </datalist>
            </div>
            <div className="w-28">
              <label className="mb-1 block text-sm font-medium text-ink">Währung</label>
              <select value={waehrung} onChange={(e) => setWaehrung(e.target.value)} className={inputKlasse}>
                {WAEHRUNGEN.map((w) => (
                  <option key={w} value={w}>
                    {w}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-ink">Barvermögen – Anfangsbestand</label>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              value={anfangsbestand}
              onChange={(e) => setAnfangsbestand(e.target.value)}
              placeholder="0,00"
              className={`${inputKlasse} text-right tabular-nums`}
            />
            <p className="mt-1 text-xs text-ink-muted">
              Startguthaben (z. B. aktueller Kontostand). Zugeordnete Buchungen werden verrechnet.
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-ink">Link</label>
            <input
              type="url"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="https://…"
              className={inputKlasse}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-ink">Notiz</label>
            <textarea
              value={notiz}
              onChange={(e) => setNotiz(e.target.value)}
              rows={2}
              className={`${inputKlasse} text-sm`}
            />
          </div>

          {fehler && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{fehler}</p>}

          <div className="flex items-center justify-between gap-3 pt-1">
            {!istNeu ? (
              <button
                type="button"
                onClick={entfernen}
                disabled={laeuft}
                className="rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-50 disabled:opacity-50"
              >
                Löschen
              </button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-line px-3 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-ink/5"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={laeuft}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-500 disabled:opacity-50"
              >
                {laeuft ? 'Speichere…' : 'Speichern'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
